import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  useMediaQuery,
} from "@mui/material";
import TableViewIcon from "@mui/icons-material/TableView";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import regionsData from "@/app/features/company/data/regions_lite.json";
import OpportunityCard from "@/app/global-components/opportunity-card";
import TableComponent from "@/app/global-components/table";
import StatusChip from "@/app/features/tender/components/view/Status";
import { isNull } from "lodash";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { routes } from "@/consts";
import { LANG } from "@/types";
import { SearchIcon } from "lucide-react";
import useOffer from "@/hooks/useTender";
import { BiPlus } from "react-icons/bi";

interface CategoryOption {
  value: any;
  label: any;
}
interface Region {
  region_id: number;
  name_ar: string;
}
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};
interface Tender {
  id: number;
  title: string;
  minValue?: number;
  value: number;
  region: string;
  city: string;
  type: string;
  status: string;
  endDate: string;
  deliverDate: string;
  categories: string[];
  TenderNr: number;
  category: string;
  location: string;
  remainingTime: number;
  companyId: string;
}

const TenderMinePage: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const isMobile = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategories, setFilterCategories] = useState<string[] | string>(
    []
  );
  const [filteredTenders, setFilteredTenders] = useState<Tender[]>([]);
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [regions, setRegions] = useState<CategoryOption[]>([]);
  const [Categoris, setCategoris] = useState<CategoryOption[]>([]);
  const itemsPerPage = 10;

  const { getMyOffers, getcategories } = useOffer();

  useEffect(() => {
    setRegions(
      regionsData.map((region: Region) => ({
        value: region.region_id.toString(),
        label: region.name_ar,
      }))
    );

    const fetchCategoris = async () => {
      try {
        const Categories = await getcategories();
        if (Categories) {
          const uniqueCategories = Categories.data.filter(
            (item: { name: any }, index: any, self: any[]) =>
              index === self.findIndex((t) => t.name === item.name)
          );
          const Cates: CategoryOption[] = uniqueCategories.map(
            (item: { id: any; name: any }) => ({
              value: item.id,
              label: item.name,
            })
          );
          setCategoris(Cates);
        } else {
          setCategoris([]);
        }
      } catch (error) {
        console.error("Error fetching Offers:", error);
      }
    };

    const fetchOffers = async () => {
      try {
        const Offers = await getMyOffers();
        if (Offers) {
          setTenders(Offers.data);
          setFilteredTenders(Offers.data);
        } else {
          setTenders([]);
        }
      } catch (error) {
        console.error("Error fetching Offers:", error);
      }
    };

    fetchCategoris();
    fetchOffers();
  }, []);

  useMemo(() => {
    const filtered = tenders.filter(
      (tender) =>
        tender.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (filterCategories.length === 0 ||
          tender.categories.some((category) =>
            filterCategories.includes(category)
          ))
    );
    setFilteredTenders(filtered);
  }, [searchTerm, filterCategories, tenders]);

  const primaryColor = "#644980";

  const getRegion = (value: string) => {
    const region = regions.find(
      (item) => parseInt(item.value) === parseInt(value)
    );
    return region ? region.label : "";
  };

  const tableColumns = [
    { title: t("orders.number"), field: "TenderNr" },
    { title: t("company.fields.address"), field: "title" },
    {
      title: t("tender.value"),
      field: "value",
      render: (row: Tender) => `${row.value} SAR`,
    },
    {
      title: t("company.fields.region"),
      field: "region",
      render: (row: Tender) => getRegion(row.region),
    },
    {
      title: t("orders.fields.status"),
      field: "status",
      render: (row: Tender) => (
        <StatusChip status={isNull(row.status) ? "Planning" : row.status} />
      ),
    },
  ];

  return (
    <div className="flex flex-col w-full">
      <main className="flex w-full flex-col">
        <section className="w-full min-h-[calc(100vh-78px)] max-w-xs bg-primary-white p-5 rounded-md xs:max-w-full py-6">
          <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center items-right md:items-right text-right">
            <h2 className="flex text-3xl font-semibold tracking-tight mb-2 text-primary-dark">
              {t("tenders.manageRequests")}
            </h2>
            <button
              style={{ color: primaryColor, marginRight: 10 }}
              onClick={() => navigate(routes.createTender)}
              title={t("actions.createNewTender")}
              className="flex bg-zinc-900 w-fit !text-white p-2 rounded-xl items-center"
            >
              <BiPlus />
              {t("actions.addTenderRequest")}
            </button>
          </div>
          <div className="flex flex-col md:justify-between md:flex-row md:items-center mb-6 gap-4">
            <TextField
              label={t("actions.search")}
              variant="outlined"
              fullWidth
              dir={locale === LANG.AR ? "rtl" : "ltr"}
              margin="normal"
              value={searchTerm}
              size="small"
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
                inputProps: { dir: locale === LANG.AR ? "rtl" : "ltr" },
              }}
              sx={{
                maxWidth: 300,
                "& .MuiInputBase-root": {
                  direction: locale === LANG.AR ? "rtl" : "ltr",
                },
                "& .MuiInputLabel-root": {
                  transformOrigin: locale === LANG.AR ? "right" : "left",
                  direction: locale === LANG.AR ? "rtl" : "ltr",
                  ...(locale === LANG.AR
                    ? { right: 30, left: "auto" }
                    : { left: 0, right: "auto" }),
                },
                "& legend": {
                  textAlign: locale === LANG.AR ? "end" : "start",
                },
              }}
            />
            <div className="flex gap-2">
              <Select
                displayEmpty
                id="categorie-multiple-name"
                input={<OutlinedInput />}
                inputProps={{ "aria-label": "Without label" }}
                className="h-10 bg-white w-36 md:w-64 xl:w-80 border !border-zinc-500"
                multiple
                sx={{
                  "& .MuiSelect-select": {
                    paddingRight: "10px !important",
                  },
                  "& .MuiSelect-icon": {
                    left: locale === LANG.EN ? "unset" : 0,
                    marginLeft: 0,
                    right: locale === LANG.EN ? 0 : "unset",
                  },
                }}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em>{t("categories.select")}</em>;
                  }
                  const value = Categoris.filter((value) =>
                    selected.includes(value.value)
                  ).map((item) => item.label);
                  return value.join(", ");
                }}
                value={filterCategories}
                onChange={(e) => setFilterCategories(e.target.value)}
                MenuProps={MenuProps}
              >
                <MenuItem disabled value="">
                  <em>{t("categories.select")}</em>
                </MenuItem>
                {Categoris.map((name) => (
                  <MenuItem
                    className="!mb-2"
                    sx={{
                      "&.Mui-selected": {
                        backgroundColor: "#000",
                        borderRadius: 15,
                        color: "#fff",
                        fontWeight: "600",
                      },
                      "&:hover.MuiMenuItem-root": {
                        backgroundColor: "#000",
                        borderRadius: 15,
                        color: "#fff",
                      },
                    }}
                    key={name.value}
                    value={name.value}
                    // style={getStyles(name, personName, theme)}
                  >
                    <span>{name.label}</span>
                  </MenuItem>
                ))}
              </Select>

              <button
                style={{
                  background: primaryColor,
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: "5px",
                  fontSize: "14px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={() => setViewMode(!viewMode)}
                title={t("actions.toggleView")}
              >
                {viewMode ? (
                  <TableViewIcon style={{ fontSize: "18px" }} />
                ) : (
                  <ViewModuleIcon style={{ fontSize: "18px" }} />
                )}
              </button>
            </div>
          </div>
          {!filteredTenders.length && (
            <div className="text-center">{t("general.NoDataAvailable")}</div>
          )}
          {viewMode ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredTenders.map((opportunity) => (
                <OpportunityCard
                  key={opportunity.id}
                  opportunity={opportunity}
                />
              ))}
            </div>
          ) : (
            <TableComponent
              columns={tableColumns}
              data={filteredTenders}
              rowKeyField="id"
              onRowClick={(row) => navigate(`${routes.vuetender}/${row.id}`)}
              pagination={true}
              rowsPerPage={itemsPerPage}
              isMobile={isMobile}
            />
          )}
        </section>
      </main>
    </div>
  );
};

export default TenderMinePage;
